/**
 * B-tree binary-index walker for hitomi's title search.
 *
 * Hitomi publishes a precomputed search index split across two files served
 * from `https://ltn.hitomi.la/galleriesindex/`:
 *
 *   - `galleries.<version>.index`  — fixed-size B-tree nodes (464 bytes each).
 *   - `galleries.<version>.data`   — packed int32-BE arrays of gallery IDs
 *                                    referenced by leaf-data pointers.
 *
 * Each search term hashes to the first 4 bytes of `SHA-256(term)`, and the
 * tree is walked by comparing those bytes (with lexicographic byte ordering)
 * against the keys stored in each node. Matches yield an `(offset, length)`
 * pointer into the data file; we Range-fetch that slice and decode it as a
 * `.nozomi`-style int32-BE buffer.
 *
 * Node wire layout (big-endian throughout):
 *
 * ```
 *   int32           keyCount
 *   repeat keyCount {
 *     int32  keySize  (1..31)
 *     bytes  key      (keySize bytes)
 *   }
 *   int32           dataCount
 *   repeat dataCount {
 *     uint64 offset   (into .data)
 *     int32  length
 *   }
 *   uint64[17]      childOffsets   (0 = no child)
 * ```
 *
 * Nodes are always padded to 464 bytes; we Range-fetch `[addr, addr + 463]`.
 */

import { NetworkError, ParseError } from '../../lib/errors.ts';
import { parseNozomi } from './nozomi.ts';
import { RESOURCE_DOMAIN } from './gg.ts';
import type { BTreeMatch, BTreeNode } from './types.ts';

/** Bytes on disk per B-tree node. */
export const BTREE_NODE_SIZE = 464;
/** Number of child pointers in each node. */
export const BTREE_CHILD_COUNT = 17;
/** Minimum allowed key length (in bytes). */
const KEY_MIN = 1;
/** Maximum allowed key length (in bytes). */
const KEY_MAX = 31;

const inflightTitleSearches = new Map<string, Promise<number[]>>();

/**
 * Decode a 464-byte node body into its keys, data pointers, and child offsets.
 *
 * Pure: takes raw bytes and returns the parsed node. Throws {@link ParseError}
 * if any field is out of range (key size 0 or >31, truncated body, etc.).
 */
export function decodeBTreeNode(buf: ArrayBuffer | ArrayBufferView): BTreeNode {
  const bytes =
    ArrayBuffer.isView(buf)
      ? new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength)
      : new Uint8Array(buf);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  if (bytes.byteLength < 4) {
    throw new ParseError('hitomi btree: node too short to contain keyCount', {
      source: 'hitomi',
    });
  }

  let offset = 0;
  const keyCount = view.getInt32(offset, false);
  offset += 4;

  const keys: Uint8Array[] = [];
  for (let i = 0; i < keyCount; i++) {
    if (offset + 4 > bytes.byteLength) {
      throw new ParseError('hitomi btree: truncated keySize', { source: 'hitomi' });
    }
    const keySize = view.getInt32(offset, false);
    offset += 4;
    if (keySize < KEY_MIN || keySize > KEY_MAX) {
      throw new ParseError(`hitomi btree: keySize ${keySize} out of [1,31]`, {
        source: 'hitomi',
      });
    }
    if (offset + keySize > bytes.byteLength) {
      throw new ParseError('hitomi btree: truncated key bytes', { source: 'hitomi' });
    }
    keys.push(bytes.slice(offset, offset + keySize));
    offset += keySize;
  }

  if (offset + 4 > bytes.byteLength) {
    throw new ParseError('hitomi btree: truncated dataCount', { source: 'hitomi' });
  }
  const dataCount = view.getInt32(offset, false);
  offset += 4;

  const datas: BTreeNode['datas'] = [];
  for (let i = 0; i < dataCount; i++) {
    if (offset + 12 > bytes.byteLength) {
      throw new ParseError('hitomi btree: truncated data pointer', { source: 'hitomi' });
    }
    const ptrOffset = view.getBigUint64(offset, false);
    const ptrLength = view.getInt32(offset + 8, false);
    datas.push({ offset: ptrOffset, length: ptrLength });
    offset += 12;
  }

  const children: bigint[] = [];
  for (let i = 0; i < BTREE_CHILD_COUNT; i++) {
    if (offset + 8 > bytes.byteLength) {
      throw new ParseError('hitomi btree: truncated child pointers', {
        source: 'hitomi',
      });
    }
    children.push(view.getBigUint64(offset, false));
    offset += 8;
  }

  return { keys, datas, children };
}

/**
 * Compare two byte arrays lexicographically — `-1` if `a < b`, `1` if `a > b`,
 * `0` if equal. Matches `Buffer.compare` semantics for the byte-by-byte case.
 */
export function compareBytes(a: Uint8Array, b: Uint8Array): number {
  const min = Math.min(a.byteLength, b.byteLength);
  for (let i = 0; i < min; i++) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av < bv) return -1;
    if (av > bv) return 1;
  }
  if (a.byteLength < b.byteLength) return -1;
  if (a.byteLength > b.byteLength) return 1;
  return 0;
}

/**
 * Walk a single node: find where `key` would slot in, return either the
 * matching data pointer (exact match) or the child offset to descend into.
 */
export function searchInNode(
  key: Uint8Array,
  node: BTreeNode,
): { kind: 'match'; data: BTreeMatch } | { kind: 'descend'; child: bigint } | { kind: 'miss' } {
  let index = 0;
  let cmp = -1;
  while (index < node.keys.length) {
    const keyAt = node.keys[index];
    if (!keyAt) break;
    cmp = compareBytes(key, keyAt);
    if (cmp !== 1) break;
    index++;
  }
  if (cmp === 0) {
    const hit = node.datas[index];
    if (!hit) return { kind: 'miss' };
    return { kind: 'match', data: hit };
  }
  // If all child pointers are 0n we're at a leaf — no further descent.
  let leaf = true;
  for (const c of node.children) {
    if (c !== 0n) {
      leaf = false;
      break;
    }
  }
  if (leaf) return { kind: 'miss' };
  const child = node.children[index];
  if (child === undefined || child === 0n) return { kind: 'miss' };
  return { kind: 'descend', child };
}

/**
 * Compute the SHA-256-prefix lookup key for a single search term. Hitomi uses
 * the first 4 bytes of `SHA-256(term)`. The term must be passed in the
 * lowercased form that the index was built with — callers handle casing.
 *
 * Uses WebCrypto (`globalThis.crypto.subtle.digest`). In a Node test env,
 * `globalThis.crypto` is exposed since Node 20.
 */
export async function hashTerm(term: string): Promise<Uint8Array> {
  const bytes = new TextEncoder().encode(term);
  const cryptoObj = globalThis.crypto as Crypto | undefined;
  if (!cryptoObj || !cryptoObj.subtle) {
    throw new ParseError('hitomi btree: WebCrypto not available', { source: 'hitomi' });
  }
  const digest = await cryptoObj.subtle.digest('SHA-256', bytes);
  return new Uint8Array(digest, 0, 4);
}

/**
 * Resolve the current index version string. Hitomi advertises it as a tiny
 * file (typically a unix timestamp); it's used to construct the
 * `galleries.<version>.index` filename. We re-fetch on every query — it's
 * less than a hundred bytes and short-circuiting from a stale version would
 * silently return broken offsets.
 */
export async function fetchIndexVersion(
  field: 'galleries' | 'languages' = 'galleries',
): Promise<string> {
  const { proxyFetch } = await import('@/lib/proxy');
  const res = await proxyFetch(`https://${RESOURCE_DOMAIN}/${field}index/version`);
  if (!res.ok) {
    throw new NetworkError(`hitomi: ${field}index/version returned ${res.status}`, {
      source: 'hitomi',
      status: res.status,
    });
  }
  const v = (await res.text()).trim();
  if (!v) {
    throw new ParseError(`hitomi: empty ${field}index/version body`, { source: 'hitomi' });
  }
  return v;
}

/**
 * Fetch and decode a single B-tree node at the given on-disk byte offset.
 *
 * The root node lives at byte offset `0`, so callers must filter sentinel
 * child pointers before asking for a node.
 */
export async function fetchNode(
  field: 'galleries',
  version: string,
  address: bigint,
): Promise<BTreeNode | undefined> {
  const start = address.toString();
  const end = (address + BigInt(BTREE_NODE_SIZE - 1)).toString();
  const url = `https://${RESOURCE_DOMAIN}/${field}index/${field}.${version}.index`;
  const { proxyFetch } = await import('@/lib/proxy');
  const res = await proxyFetch(url, {
    headers: { Range: `bytes=${start}-${end}` },
  });
  if (res.status !== 200 && res.status !== 206) {
    if (res.status === 416 || res.status === 404) return undefined;
    throw new NetworkError(`hitomi: index Range fetch ${res.status}`, {
      source: 'hitomi',
      status: res.status,
    });
  }
  const buf = await res.arrayBuffer();
  if (buf.byteLength === 0) return undefined;
  return decodeBTreeNode(buf);
}

/**
 * Binary-search the B-tree for a single term's key. Returns the matching
 * data pointer, or `undefined` if the term isn't indexed.
 *
 * `version` is the result of {@link fetchIndexVersion}; reusing the same
 * version across multiple `binarySearchKey` calls avoids per-term version
 * fetches.
 */
export async function binarySearchKey(
  key: Uint8Array,
  version: string,
): Promise<BTreeMatch | undefined> {
  let node = await fetchNode('galleries', version, 0n);
  if (!node) return undefined;
  // Bound the descent: real-world trees rarely exceed 20 levels; we cap at
  // 64 as defence against a poisoned response causing an infinite loop.
  for (let depth = 0; depth < 64; depth++) {
    const step = searchInNode(key, node);
    if (step.kind === 'match') return step.data;
    if (step.kind === 'miss') return undefined;
    const next = await fetchNode('galleries', version, step.child);
    if (!next) return undefined;
    node = next;
  }
  return undefined;
}

/**
 * Fetch the gallery-IDs slice referenced by a single {@link BTreeMatch}.
 *
 * Hitomi's `.data` file stores each leaf as `int32 BE length, int32 BE id, …`.
 * Upstream skips the first 4 bytes (the embedded length) and reads the next
 * `length - 4` bytes. We follow that same convention.
 */
export async function fetchDataIds(
  match: BTreeMatch,
  version: string,
): Promise<number[]> {
  const startBig = match.offset + 4n;
  const endBig = match.offset + BigInt(match.length) - 1n;
  if (endBig < startBig) return [];
  const url = `https://${RESOURCE_DOMAIN}/galleriesindex/galleries.${version}.data`;
  const { proxyFetch } = await import('@/lib/proxy');
  const res = await proxyFetch(url, {
    headers: { Range: `bytes=${startBig.toString()}-${endBig.toString()}` },
  });
  if (res.status !== 200 && res.status !== 206) {
    throw new NetworkError(`hitomi: data Range fetch ${res.status}`, {
      source: 'hitomi',
      status: res.status,
    });
  }
  const buf = await res.arrayBuffer();
  return parseNozomi(buf);
}

/**
 * High-level: given a free-form search string, return the set of gallery IDs
 * that contain ALL the whitespace-separated terms.
 *
 * Term intersection follows hitomi: any term that doesn't resolve to a leaf
 * collapses the result to `[]`. We sort intersections by the smallest set
 * first to minimize work.
 */
export async function searchTitleTerms(query: string): Promise<number[]> {
  const lowered = query.toLowerCase().trim();
  if (!lowered) return [];
  const existing = inflightTitleSearches.get(lowered);
  if (existing) return existing;

  const promise = searchTitleTermsUncached(lowered).finally(() => {
    inflightTitleSearches.delete(lowered);
  });
  inflightTitleSearches.set(lowered, promise);
  return promise;
}

async function searchTitleTermsUncached(lowered: string): Promise<number[]> {
  const terms = lowered.split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];

  const version = await fetchIndexVersion('galleries');

  const idSets: Set<number>[] = [];
  for (const term of terms) {
    const key = await hashTerm(term);
    const match = await binarySearchKey(key, version);
    if (!match) return [];
    const ids = await fetchDataIds(match, version);
    if (ids.length === 0) return [];
    idSets.push(new Set(ids));
  }
  idSets.sort((a, b) => a.size - b.size);
  const seed = idSets[0];
  if (!seed) return [];
  const result: number[] = [];
  for (const id of seed) {
    let inAll = true;
    for (let i = 1; i < idSets.length; i++) {
      const s = idSets[i];
      if (!s || !s.has(id)) {
        inAll = false;
        break;
      }
    }
    if (inAll) result.push(id);
  }
  return result;
}
